import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Radio,
  TrendingUp,
  ScrollText,
  Lock,
  AlertTriangle,
} from "lucide-react";
import { API } from "../../lib/api";

function fmtAmerican(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Math.round(Number(n));
  return v > 0 ? `+${v}` : `${v}`;
}

function StartTimeCompact({ iso }) {
  if (!iso) return <span className="text-zinc-600">—</span>;
  const d = new Date(iso);
  const now = new Date();
  const deltaMin = Math.round((d - now) / 60000);
  const absolute = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  let relative;
  if (deltaMin < 0) relative = "live";
  else if (deltaMin < 60) relative = `${deltaMin}m`;
  else if (deltaMin < 1440) relative = `${Math.round(deltaMin / 60)}h`;
  else relative = `${Math.round(deltaMin / 1440)}d`;
  return (
    <div className="mono text-[10px]">
      <div className="text-zinc-300">{absolute}</div>
      <div className="text-zinc-500">{relative}</div>
    </div>
  );
}

function SimRange({ pct, low, high }) {
  const p = Number(pct) || 0;
  const l = Number(low) || 0;
  const h = Number(high) || 0;
  if (!h) return <span className="text-zinc-600 mono text-[10px]">—</span>;
  return (
    <div className="w-20">
      <div className="mono text-[10px] text-zinc-400 mb-0.5">
        {l.toFixed(0)}–{h.toFixed(0)}%
      </div>
      <div className="relative h-1 bg-zinc-900 border border-zinc-800">
        <div
          className="absolute top-0 h-full bg-[#00D4FF]/40"
          style={{ left: `${l}%`, width: `${Math.max(0, h - l)}%` }}
        />
        <div
          className="absolute top-[-2px] h-[6px] w-[2px] bg-[#00D4FF]"
          style={{ left: `${p}%` }}
        />
      </div>
    </div>
  );
}

function ResultBadge({ r }) {
  if (r === "win") return <span className="mono text-[10px] px-1.5 py-0.5 border border-[#00D4FF] text-[#00D4FF]">WIN</span>;
  if (r === "loss") return <span className="mono text-[10px] px-1.5 py-0.5 border border-red-500 text-red-400">LOSS</span>;
  return <span className="mono text-[10px] px-1.5 py-0.5 border border-zinc-600 text-zinc-400">PUSH</span>;
}

function Stat({ label, value, tone = "neutral" }) {
  const toneClass = tone === "good" ? "text-[#00D4FF]" : tone === "warn" ? "text-[#FFBF00]" : "text-zinc-200";
  return (
    <div className="flex items-baseline gap-2 whitespace-nowrap">
      <span className="overline text-zinc-500">{label}</span>
      <span className={`mono text-sm font-medium ${toneClass}`}>{value}</span>
    </div>
  );
}

function TickerStrip({ ticker }) {
  const be = ticker?.best_edge || {};
  const led = ticker?.ledger || {};
  return (
    <div
      className="border border-zinc-800 bg-zinc-950/70 px-5 py-3 flex flex-wrap items-center gap-5 text-xs"
      data-testid="landing-ticker"
    >
      <div className="flex items-center gap-1.5 text-[#00D4FF]">
        <Radio size={10} className="animate-pulse" />
        <span className="mono font-medium tracking-wider">LIVE TERMINAL</span>
      </div>
      <Stat label="SLATE" value={ticker?.slate_count ?? "—"} />
      <Stat label="+EV" value={ticker?.plus_ev_count ?? 0} tone="good" />
      <Stat label="TRAPS" value={ticker?.traps_count ?? 0} tone="warn" />
      <div className="hidden md:flex items-baseline gap-2 border-l border-zinc-800 pl-5 min-w-0">
        <span className="overline text-zinc-500">BEST</span>
        <span className="mono text-sm text-zinc-200 truncate max-w-[18rem]">
          {be.lean || "Evaluating…"}
        </span>
        {Number.isFinite(be.edge_pct) && (
          <span className="mono text-sm text-[#00D4FF]">
            {be.edge_pct > 0 ? `+${be.edge_pct.toFixed(1)}%` : `${be.edge_pct.toFixed(1)}%`}
          </span>
        )}
      </div>
      <div className="hidden lg:flex items-baseline gap-2 border-l border-zinc-800 pl-5">
        <span className="overline text-zinc-500">LEDGER</span>
        <span className="mono text-sm">
          <span className="text-[#00D4FF]">{led.wins || 0}W</span>
          <span className="text-zinc-500"> / </span>
          <span className="text-red-400">{led.losses || 0}L</span>
          {Number.isFinite(led.hit_rate_pct) && (
            <>
              <span className="text-zinc-500"> · </span>
              <span className="text-zinc-200">{led.hit_rate_pct.toFixed(1)}%</span>
            </>
          )}
          {led.avg_clv_pct !== null && led.avg_clv_pct !== undefined && (
            <>
              <span className="text-zinc-500"> · CLV </span>
              <span className={led.avg_clv_pct >= 0 ? "text-[#00D4FF]" : "text-red-400"}>
                {led.avg_clv_pct >= 0 ? `+${led.avg_clv_pct}` : led.avg_clv_pct}%
              </span>
            </>
          )}
        </span>
      </div>
    </div>
  );
}

function EdgeRow({ r, locked }) {
  const leanIsHome = r.lean && r.home_name && r.lean.toLowerCase().includes(r.home_name.toLowerCase());
  const modelPct = leanIsHome ? r.sim_model_home_pct : r.sim_model_away_pct;
  const low = leanIsHome ? r.sim_home_low : r.sim_away_low;
  const high = leanIsHome ? r.sim_home_high : r.sim_away_high;
  const fair = leanIsHome ? r.fair_home : r.fair_away;
  const dk = leanIsHome ? r.dk_home : r.dk_away;
  return (
    <tr
      className={locked ? "select-none" : "hover:bg-zinc-900/40"}
      data-testid={locked ? "landing-edge-row-locked" : "landing-edge-row"}
    >
      <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900">
        <div className={locked ? "blur-sm" : ""}>
          <StartTimeCompact iso={r.start_time} />
        </div>
      </td>
      <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900">
        <div className={locked ? "blur-sm" : ""}>
          <div className="text-zinc-200 truncate max-w-[180px]">{r.short_name || r.matchup}</div>
          <div className="text-[10px] text-zinc-500 uppercase">{r.sport}</div>
        </div>
      </td>
      <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900 text-[#00D4FF] truncate max-w-[180px]">
        <div className={locked ? "blur-sm" : ""}>{r.lean}</div>
      </td>
      <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900">
        <div className={locked ? "blur-sm" : ""}>
          <SimRange pct={modelPct} low={low} high={high} />
        </div>
      </td>
      <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900 text-right text-[#00D4FF]">
        <span className={locked ? "blur-sm inline-block" : ""}>{fmtAmerican(fair)}</span>
      </td>
      <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900 text-right text-zinc-300">
        <span className={locked ? "blur-sm inline-block" : ""}>{fmtAmerican(dk)}</span>
      </td>
      <td className="px-3 py-2.5 mono text-xs border-b border-zinc-900 text-right text-[#00D4FF] font-medium">
        <span className={locked ? "blur-sm inline-block" : ""}>
          {Number.isFinite(r.edge_pct) ? (r.edge_pct > 0 ? `+${r.edge_pct.toFixed(1)}%` : `${r.edge_pct.toFixed(1)}%`) : "—"}
        </span>
      </td>
    </tr>
  );
}

function PaywallBanner({ count, locks24h }) {
  return (
    <div
      className="absolute inset-x-0 bottom-0 pt-20 pb-5 px-5 bg-gradient-to-t from-[#0b0d12] via-[#0b0d12]/90 to-transparent flex flex-col items-center gap-2"
      data-testid="landing-paywall-banner"
    >
      {Number.isFinite(locks24h) && locks24h > 0 && (
        <div className="mono text-[10px] text-zinc-400 flex items-center gap-1.5" data-testid="landing-social-proof-banner">
          <span className="w-1.5 h-1.5 bg-[#00D4FF] rounded-full animate-pulse" />
          {locks24h.toLocaleString()} bettor{locks24h === 1 ? "" : "s"} locked plays in the last 24h
        </div>
      )}
      <Link
        to="/register"
        data-testid="landing-paywall-cta"
        className="inline-flex items-center gap-2 px-5 py-3 bg-[#00D4FF] text-black mono uppercase tracking-widest text-xs font-medium hover:brightness-110 shadow-[0_0_24px_rgba(0,212,255,0.3)]"
      >
        <Lock size={12} strokeWidth={2} />
        Unlock {count} more {count === 1 ? "play" : "plays"} · 3 days free
        <ArrowRight size={12} strokeWidth={1.75} />
      </Link>
    </div>
  );
}

function BestEdgeBlock({ data, locks24h }) {
  const unlocked = data?.unlocked || [];
  const locked = data?.locked || [];
  const lockedCount = data?.locked_count || 0;

  if (!unlocked.length && !locked.length) {
    return (
      <div className="border border-dashed border-zinc-800 p-6 text-center mono text-[11px] text-zinc-500">
        Next slate evaluates on the top of the hour.
      </div>
    );
  }

  return (
    <div className="relative border border-zinc-800 overflow-hidden">
      <table className="w-full" data-testid="landing-best-edge-table">
        <thead>
          <tr>
            <th className="px-3 py-2 overline text-left bg-zinc-950 border-b border-zinc-800">TIME</th>
            <th className="px-3 py-2 overline text-left bg-zinc-950 border-b border-zinc-800">MATCHUP</th>
            <th className="px-3 py-2 overline text-left bg-zinc-950 border-b border-zinc-800">LEAN</th>
            <th className="px-3 py-2 overline text-left bg-zinc-950 border-b border-zinc-800">SIM RANGE</th>
            <th className="px-3 py-2 overline text-right bg-zinc-950 border-b border-zinc-800">FAIR</th>
            <th className="px-3 py-2 overline text-right bg-zinc-950 border-b border-zinc-800">DK</th>
            <th className="px-3 py-2 overline text-right bg-zinc-950 border-b border-zinc-800">EDGE</th>
          </tr>
        </thead>
        <tbody>
          {unlocked.map((r) => <EdgeRow key={r.key} r={r} locked={false} />)}
          {locked.map((r) => <EdgeRow key={`locked-${r.key}`} r={r} locked={true} />)}
        </tbody>
      </table>
      {lockedCount > 0 && <PaywallBanner count={lockedCount} locks24h={locks24h} />}
    </div>
  );
}

function TrapsTeaserBlock({ data }) {
  const unlocked = data?.unlocked || [];
  const locked = data?.locked || [];
  const lockedCount = data?.locked_count || 0;

  if (!unlocked.length && !locked.length) {
    return (
      <div className="border border-zinc-800 p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={14} className="amber-text" strokeWidth={1.5} />
          <div className="overline">PUBLIC·TRAPS · TODAY</div>
        </div>
        <p className="mono text-[11px] text-zinc-500">No traps flagged. Sharps and squares are aligned.</p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 p-5 relative overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="amber-text" strokeWidth={1.5} />
          <div className="overline">PUBLIC·TRAPS · TODAY</div>
        </div>
        {lockedCount > 0 && (
          <span className="mono text-[10px] text-zinc-500">+{lockedCount} more</span>
        )}
      </div>
      <ul className="space-y-2.5">
        {unlocked.map((t) => (
          <li key={t.key} className="flex items-center justify-between gap-3" data-testid="landing-trap-row">
            <div className="min-w-0 flex-1">
              <div className="mono text-xs text-zinc-200 truncate">{t.short_name || t.matchup}</div>
              <div className="mono text-[10px] text-zinc-500">
                Public on <span className="amber-text">{(t.public_side || "?").toUpperCase()}</span> · sharps fade →
                <span className="text-[#00D4FF]"> {(t.sharp_side || "?").toUpperCase()}</span>
              </div>
            </div>
            <div className="mono text-xs text-amber-400 shrink-0">TRAP {t.trap_score || 0}</div>
          </li>
        ))}
        {locked.map((t) => (
          <li key={`locked-${t.key}`} className="flex items-center justify-between gap-3 select-none" data-testid="landing-trap-row-locked">
            <div className="min-w-0 flex-1 blur-sm">
              <div className="mono text-xs text-zinc-200 truncate">{t.short_name || t.matchup}</div>
              <div className="mono text-[10px] text-zinc-500">
                Public on {(t.public_side || "?").toUpperCase()} · sharps fade → {(t.sharp_side || "?").toUpperCase()}
              </div>
            </div>
            <div className="mono text-xs text-amber-400 shrink-0 blur-sm">TRAP {t.trap_score || 0}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LedgerPreviewList({ picks }) {
  if (!picks?.length) {
    return (
      <div className="border border-dashed border-zinc-800 p-6 text-center mono text-[11px] text-zinc-500">
        First graded picks will appear here tonight.
      </div>
    );
  }
  return (
    <div className="border border-zinc-800 divide-y divide-zinc-900">
      {picks.map((p) => (
        <div key={p.key} className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="mono text-xs text-zinc-200 truncate">{p.lean}</div>
            <div className="mono text-[10px] text-zinc-500 truncate">
              {p.short_name || p.matchup} · {p.final_score || "—"}
            </div>
          </div>
          <ResultBadge r={p.result} />
          <div className={`mono text-xs text-right w-16 ${p.clv_pct === null || p.clv_pct === undefined ? "text-zinc-500" : p.clv_pct >= 0 ? "text-[#00D4FF]" : "text-red-400"}`}>
            {p.clv_pct === null || p.clv_pct === undefined ? "CLV —" : `CLV ${p.clv_pct >= 0 ? "+" : ""}${p.clv_pct}%`}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LandingTerminalPreview() {
  const [data, setData] = useState(null);
  const [ticker, setTicker] = useState(null);

  useEffect(() => {
    const c = axios.CancelToken.source();
    Promise.all([
      axios.get(`${API}/terminal/teaser`, { cancelToken: c.token }).catch(() => ({ data: null })),
      axios.get(`${API}/terminal/ticker`, { cancelToken: c.token }).catch(() => ({ data: null })),
    ]).then(([t, tk]) => {
      setData(t?.data || null);
      setTicker(tk?.data || null);
    });
    return () => c.cancel();
  }, []);

  return (
    <section
      className="relative z-10 border-y border-zinc-800 py-20 bg-zinc-950/30"
      data-testid="landing-terminal-preview"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="overline neon-text mb-2 flex items-center gap-2">
              <Radio size={10} className="animate-pulse" />
              LIVE · NO LOGIN REQUIRED
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-tight">
              See the tape.{" "}
              <span className="text-[#00D4FF]">Unlock the plays.</span>
            </h2>
            <p className="text-zinc-400 mt-4 max-w-2xl text-sm leading-relaxed">
              Below is the live terminal our subscribers stare at all day —
              ranked edges, public traps, and every pick we've ever graded.
              The first plays are on us. The rest are 3 days free.
            </p>
          </div>
          <Link
            to="/ledger"
            data-testid="landing-view-ledger"
            className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-700 mono text-xs tracking-widest uppercase hover:border-[#00D4FF] hover:text-[#00D4FF]"
          >
            <ScrollText size={12} /> Public Ledger
          </Link>
        </div>

        {/* Ticker strip */}
        <TickerStrip ticker={ticker} />

        {/* Two-column capability grid */}
        <div className="grid lg:grid-cols-5 gap-6 mt-6">
          {/* Left (3/5): Best Edge with paywall */}
          <div className="lg:col-span-3 space-y-5">
            <div className="border border-zinc-800 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-[#00D4FF]" strokeWidth={1.5} />
                  <div className="overline">BEST EDGE · TODAY</div>
                </div>
                <span className="mono text-[10px] text-zinc-500">ranked by model +EV</span>
              </div>
              <BestEdgeBlock data={data?.best_edge} locks24h={data?.social_proof?.locks_24h} />
            </div>
            <p className="mono text-[10px] text-zinc-500 px-1">
              Each play includes a 1,000-game Monte Carlo sim, a data-quality
              score, a no-bet check, and a trap-detector flag. Subscribers
              get the full slate, the Claude write-up, and one-click LOCK to
              their tracker.
            </p>
          </div>

          {/* Right (2/5): Traps + Ledger + Unlock */}
          <div className="lg:col-span-2 space-y-5">
            <TrapsTeaserBlock data={data?.traps} />

            <div className="border border-zinc-800 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ScrollText size={14} className="text-[#00D4FF]" strokeWidth={1.5} />
                  <div className="overline">GRADED · LAST 3</div>
                </div>
                <Link to="/ledger" className="mono text-[10px] text-zinc-500 hover:text-[#00D4FF] uppercase tracking-wider">
                  all →
                </Link>
              </div>
              <LedgerPreviewList picks={data?.recent_graded || []} />
            </div>

            <div className="border border-[#00D4FF]/40 p-5 bg-[#00D4FF]/5">
              <div className="flex items-center justify-between mb-3">
                <div className="overline neon-text">UNLOCK · 3 DAYS FREE</div>
                {Number.isFinite(data?.social_proof?.locks_24h) && data.social_proof.locks_24h > 0 && (
                  <div
                    className="mono text-[10px] text-zinc-400 flex items-center gap-1.5"
                    data-testid="landing-social-proof-panel"
                  >
                    <span className="w-1.5 h-1.5 bg-[#00D4FF] rounded-full animate-pulse" />
                    {data.social_proof.locks_24h.toLocaleString()} locks · 24h
                  </div>
                )}
              </div>
              <ul className="space-y-1.5 text-sm text-zinc-300 mb-5">
                {[
                  "Full slate — 10+ ranked plays, refreshed every 3 hours",
                  "Public Trap Detector + Market Disagreement Radar",
                  "1,000-game Monte Carlo + data-quality score",
                  "One-click LOCK to your Martingale tracker",
                  "CLV-graded after every close",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="text-[#00D4FF] mono shrink-0">▸</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                data-testid="landing-preview-cta"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#00D4FF] text-black mono uppercase tracking-widest text-xs font-medium hover:brightness-110 w-full justify-center"
              >
                Start 3 free days <ArrowRight size={14} strokeWidth={1.75} />
              </Link>
              <p className="mono text-[10px] text-zinc-500 mt-3 text-center">
                Then $9.99/wk · cancel anytime · no card-not-charged tricks
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
